import { useState } from "react";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";

const PARTNERSHIP_TYPES = [
  { id: "schools",     label: "مدارس", icon: "🏫", desc: "تَكاملٌ مع منهج اللغة العربيّة." },
  { id: "universities", label: "جامعات", icon: "🎓", desc: "موادّ مُساعدةٌ للأقسام اللغويّة." },
  { id: "centers",     label: "مراكز ودَور تَعلّم", icon: "📚", desc: "تَدريبٌ مكثّفٌ على البلاغة." },
  { id: "ngo",         label: "جمعيّات ثقافيّة", icon: "🌙", desc: "مبادراتٌ ثقافيّةٌ مُشتَرَكة." },
  { id: "media",       label: "إعلام ونَشر", icon: "📰", desc: "تَدريبُ كُتّابٍ وصحفيّين." },
  { id: "other",       label: "تَعاونٌ آخر", icon: "💡", desc: "اقتراحُ شراكةٍ خاصّة." },
];

export default function PartnershipsPage() {
  const stats = useStats();
  const [form, setForm] = useState({
    name: "",
    organization: "",
    email: "",
    phone: "",
    partnership_type: "schools",
    message: "",
  });
  const [status, setStatus] = useState({ kind: "idle", msg: "" });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      setStatus({ kind: "error", msg: "الرجاء إدخال الاسم." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setStatus({ kind: "error", msg: "الرجاء إدخال بريد إلكترونيّ صحيح." });
      return;
    }
    if (form.message.trim().length < 10) {
      setStatus({ kind: "error", msg: "الرسالة قصيرةٌ جدًّا." });
      return;
    }
    setStatus({ kind: "sending", msg: "جارٍ الإرسال…" });
    try {
      await api.requestPartnership(form);
      setStatus({
        kind: "success",
        msg: "✅ تمّ استلامُ طلبك. سيَتواصل معك فريقُ بَلاغة قريبًا.",
      });
      setForm({
        name: "",
        organization: "",
        email: "",
        phone: "",
        partnership_type: form.partnership_type,
        message: "",
      });
    } catch (err) {
      setStatus({ kind: "error", msg: "تعذّر إرسال الطلب. حاول مرّةً أخرى." });
    }
  };

  return (
    <div className="shell has-bottom-nav" data-testid="partnerships-page">
      <div className="page-header partnership-hero">
        <div className="hero-emblem">🤝</div>
        <h1>
          شَراكاتٌ <span className="gold">مؤسّسيّة</span>
        </h1>
        <p className="hero-sub">
          نَفتح أبوابنا للجامعات والمدارس والمراكز الراغبة في إدماج البلاغة
          العربيّة الذكيّة في برامجها.
        </p>
      </div>

      {/* ===== WHY PARTNER ===== */}
      <section className="partnership-card" data-testid="partnership-why">
        <h2>🌟 لماذا الشراكة مع بَلاغة؟</h2>
        <ul className="partnership-why">
          <li>🎯 منهجٌ مُحكَمٌ من {stats.tracks} مسارات + {stats.lessons} درسًا تفاعليًّا.</li>
          <li>🤖 معلّم ذكاءٍ اصطناعيٍّ يُقَيّم كتابات الطلّاب فرديًّا.</li>
          <li>🏅 شهاداتٌ موثّقة برقمٍ فريد + خانة QR للتحقّق المؤسّسيّ.</li>
          <li>📊 لوحاتُ متابعةٍ لتَقدّم الكوادر/الطلّاب.</li>
          <li>📜 محتوًى مُستَنَدٌ إلى القرآن والشعر العربيّ الأصيل.</li>
        </ul>
      </section>

      {/* ===== TYPES ===== */}
      <section className="partnership-card" data-testid="partnership-types">
        <h2>🧩 أَنواع التَعاون المتاحة</h2>
        <div className="partnership-types">
          {PARTNERSHIP_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`partnership-type ${form.partnership_type === t.id ? "active" : ""}`}
              onClick={() =>
                setForm((f) => ({ ...f, partnership_type: t.id }))
              }
              data-testid={`partnership-type-${t.id}`}
            >
              <span className="ptype-icon">{t.icon}</span>
              <strong>{t.label}</strong>
              <em>{t.desc}</em>
            </button>
          ))}
        </div>
      </section>

      {/* ===== FORM ===== */}
      <form
        className="partnership-card"
        onSubmit={onSubmit}
        data-testid="partnership-form"
      >
        <h2>📨 نَموذج طلب الشراكة</h2>
        <div className="form-grid-2">
          <label>
            <span>الاسم الكامل *</span>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              required
              data-testid="partnership-input-name"
            />
          </label>
          <label>
            <span>اسم المؤسّسة</span>
            <input
              type="text"
              value={form.organization}
              onChange={update("organization")}
              data-testid="partnership-input-org"
            />
          </label>
          <label>
            <span>البريد الإلكترونيّ *</span>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              required
              data-testid="partnership-input-email"
            />
          </label>
          <label>
            <span>الهاتف</span>
            <input
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              data-testid="partnership-input-phone"
            />
          </label>
        </div>
        <label>
          <span>تَفاصيل الشراكة *</span>
          <textarea
            value={form.message}
            onChange={update("message")}
            required
            minLength={10}
            rows={5}
            placeholder="عرّفنا بمؤسّستك ونوع التَعاون الذي تَطلبه…"
            data-testid="partnership-input-message"
          />
        </label>

        {status.kind !== "idle" && (
          <div
            className={`contact-status contact-status-${status.kind}`}
            data-testid="partnership-status"
          >
            {status.msg}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-gold contact-submit"
          disabled={status.kind === "sending"}
          data-testid="partnership-submit-btn"
        >
          {status.kind === "sending" ? "جارٍ الإرسال…" : "إرسال طلب الشراكة"}
        </button>
      </form>
    </div>
  );
}
